import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Media from 'components/common/Media'
import Text from 'components/common/Text/Text'
import Icon from 'components/common/Icon/Icon'
import Link, { A } from 'components/common/Link/Link'
import Theme from 'components/common/Theme/Theme'
import PhoneLink from 'components/common/PhoneLink'
import { composeFormattedPhoneNumberFromObject } from 'helpers/phone-number'
import {
  REACH_OUT_REQUEST_TITLE_NEW,
  APPOINTMENT_CONFIRMATION
} from 'constant'
import DurationInfo from './../DurationInfo'

const RequestSentBlock = React.memo(({
  boxFields,
  rowFields,
  storeContactDetails,
  firstName,
  storeDetailsUrl
}) => {
  const renderBoxElements = boxFields.map((element, idx) => (
    <DetailsDisplayBox
      className="appointment-info-field"
      fieldType={ element.fieldType }
      key={ `${idx}-${element.fieldType}` }>
      <DisplayBoxTitle
        className="appointment-info-field-label"
        idx={ idx }>{element.name}</DisplayBoxTitle>
      {element.fieldType !== 'store' ? (
        <DisplayBoxContent
          className="appointment-info-field-value"
          id={ element.fieldType } >{element.value}</DisplayBoxContent>
      ) : (
        <CustomLink
          className="appointment-info-field-value"
          id={ element.fieldType }
          to={ storeDetailsUrl }>
          {element.value} <ArrowIcon asset="caret-blue-right" alt="Arrow" />
        </CustomLink>
      )}
    </DetailsDisplayBox>
  ))

  const renderRowElements = rowFields.map((element, idx) => element.value
    ? (
      <Row
        className="appointment-info-row"
        key={ `${idx}-${element.name}` }
        rowType={ element.rowType }
        idx={ idx }>
        <FieldName className="appointment-info-field-label">{element.name}</FieldName>
        {element.rowType !== 'promotions' ? (
          <List
            className="appointment-info-list"
            showBullets={ element.showBullets }>
            {element.value.map((listItem, idx) => (
              <ListItem
                className="appointment-info-list-item"
                id={ `${element.rowType}-${element.idList[idx]}` }
                key={ `${idx}-${listItem}` }>
                {listItem}
              </ListItem>
            ))}
          </List>
        ) : (
          <List
            className="appointment-info-list"
            showBullets={ element.showBullets }>
            {element.value.map((listItem, idx) => (
              <ListItem
                className="appointment-info-list-item"
                id={ `${element.rowType}-${element.idList[idx]}` }
                key={ `${idx}-${listItem.description}` }>
                {listItem.description}
                {listItem.expirationDate && (
                  <>
                    <br />
                    {`Present offer in store. Expires ${listItem.expirationDate}`}
                  </>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Row>)
    : null)

  const renderStoreContactDetails = storeContactDetails.map((contact, idx) => (
    <ContactWrapper key={ `${idx}-${contact.asset}` } contactType={ contact.asset }>
      <ContactIcon asset={ contact.asset } alt={ contact.alt } />
      {contact.asset === 'phone' ? (
        <CustomPhoneLink number={ composeFormattedPhoneNumberFromObject(contact.value) } />
      ) : (
        <CustomEmailLink href={ `mailto:${contact.value}` } target="_self">
          {contact.value}
        </CustomEmailLink>
      )}
    </ContactWrapper>
  ))


  return (
    <Container>
      <ContentWrapper>
        <Title>
          Thanks {firstName}, <br />{APPOINTMENT_CONFIRMATION}
        </Title>
        <AppointmentDetailsWrapper>
          {renderBoxElements}
        </AppointmentDetailsWrapper>
        {renderRowElements}
        <CallContainer>
          <Text>
            {REACH_OUT_REQUEST_TITLE_NEW}
          </Text>
          <ContactWrapperContainer>
            {renderStoreContactDetails}
          </ContactWrapperContainer>
        </CallContainer>
        <DurationDisclaimer />
      </ContentWrapper>
    </Container>
  )
})

export default RequestSentBlock

RequestSentBlock.propTypes = {
  boxFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
      fieldType: PropTypes.string
    })
  ),
  firstName: PropTypes.string,
  rowFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
      showBullets: PropTypes.bool
    })
  ),
  storeContactDetails: PropTypes.arrayOf(
    PropTypes.shape({
      asset: PropTypes.string,
      alt: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string])
    })
  ),
  storeDetailsUrl: PropTypes.string
}

const Container = styled.div`
  width: 70%;
  margin: 0 auto;
  ${Media.tabletNarrow`
    width: 100%;
    padding: 0 20px;
  `}

  @media (min-width: 1024px) and (max-width: 1199px) {
    width: 80%;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
const Title = styled(Text)`
  ${Media.mobile`
    font-size: 22px;
  `}
  font-weight: 700;
  font-size: 40px;
  text-transform: uppercase;
  margin-top: 64px;
  margin-left: 20px;
  ${Media.tabletNarrow`
    margin-top: 40px;
  `}
`
const CustomLink = styled(props => <Link { ...props } />)`
  color: ${Theme.colors.blue};
  line-height: 24px;
`
const ArrowIcon = styled(Icon)`
  padding-bottom: 2px;
  margin-left: 3px;
  width: 7px;
`
const AppointmentDetailsWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  flex-wrap: wrap;
  ${Media.tabletNarrow`
    flex-direction: column;
  `}
`
const DetailsDisplayBox = styled.div`
  padding: 10px 19px;
  background-color: ${Theme.colors.off_white};
  margin-right: 20px;
  ${({ fieldType }) => `
    flex: ${fieldType === 'store' ? 2 : 0};
    flex-basis: fit-content;
  `}
  flex-direction: column;
  display: flex;
  ${Media.tabletNarrow`
    margin-bottom: ${({ fieldType }) => fieldType !== 'store' ? '12px' : 0};
    margin-right: 0;
  `}

  ${Media.tabletMedium`
    margin-bottom: ${({ fieldType }) => fieldType !== 'store' ? '16px' : 0};
    margin-right: 16px;
  `}
`
const DisplayBoxTitle = styled(Text)`
  display: block;
  font-weight: 700;
  line-height: 24px;
  min-width: ${({ idx }) => idx === 0 ? '177px' : '114px'};
  margin-bottom: 4px;
`
const DisplayBoxContent = styled(Text)`
  line-height: 24px;
`

const Row = styled.div`
  ${({ idx }) => `padding-top: ${idx === 0 ? 12 : 0}px;`}
  margin-top: 24px;
  border-bottom: 1px solid ${Theme.colors.light_grey};
  ${({ rowType }) => `
    padding-bottom: ${rowType === 'personal' ? 48 : 24}px;
  `}

  ${Media.tabletNarrow`
    padding-bottom: ${({ rowType }) => rowType === 'personal' ? 40 : 16}px;
  `}
`

const FieldName = styled.div`
  font-weight: 700;
  line-height: 24px;
  margin-left: 20px;
  margin-bottom: 4px;
`
const List = styled.ul`
  margin: 0;
  padding: 0;
  padding-left: 20px;

  & > li::before {
    content: '•';
    color: ${Theme.colors.grey};
    font-weight: bold;
    display: inline-block;
    width: 14px;
    margin-left: -14px;
    font-size: 15px;
    ${({ showBullets }) => !showBullets && css`
      display: none;
    `}
  }
`
const ListItem = styled.li`
  line-height: 24px;
`
const CustomPhoneLink = styled(PhoneLink)`
  font-weight: 700;
  margin-left: 12px;
`
const CustomEmailLink = styled(A)`
  font-weight: 700;
  margin-left: 9px;
`
const ContactWrapper = styled.span`
  padding-left: 25px;
  ${Media.mobile`
    padding-left: ${({ contactType }) => contactType === 'phone' && 0};
  `}

  ${Media.tabletNarrow`
    ${({ contactType }) => contactType !== 'phone' && css`
        padding-left: 0;
        padding-top: 8px;
    `}
  `}
`
const CallContainer = styled.div`
  margin: 24px 0 0 20px;
  display: flex;
  flex-wrap: wrap;
  ${Media.tabletNarrow`
    margin-top: 16px;
  `}
`
const ContactIcon = styled(Icon)`
  height: 13px;
  width: 13px;
  margin-bottom: 2px;
`
const ContactWrapperContainer = styled.div`
  display: flex;
  ${Media.mobile`
    width: 500px;
    padding-top: 12px;
  `}

  ${Media.tabletNarrow`
    flex-direction: column;
  `}
`

const DurationDisclaimer = styled(DurationInfo)`
  margin-left: 20px;
`
